import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { toCamelCase, toSnakeCase } from '@/lib/case';

import { useAuthAxios } from '@/hooks/useAuthAxios';

import {
    CustomHeader,
    CustomHeaderSchema,
    DocGtStatus,
    GtBulkMarkArgs,
    GtBulkMarkResponse,
    GtCoverageStats,
    GtCreateDocsCheck,
    GtCreatedDoc,
    GtDraftReviewStats,
    GtFileDoc,
    GtHubUploadResponse,
    GtMarkDocArgs,
    GtOverviewParams,
    GtOverviewResponse,
    GtPageCountResponse,
    GtFileVersion,
    GtLeftOutPages,
    GtPageSplit,
    GtPageSplitInput,
    GtRow,
    GtRowPayload,
    GtSaveRequest,
    GtSheetSplit,
    GtSheetSplitInput,
    GtExportCheckReport,
    GtSnapshot,
    GtSnapshotExportArgs,
    GtSnapshotJobResponse,
    GtSnapshotRestoreArgs,
    GtUncoveredAnswer,
    RowModelSchema,
    SnapshotRunStatus,
} from './types';

/**
 * React Query READ + WRITE hooks for the Ground Truth hub.
 *
 * All endpoints live under `/api/ground-truth` and speak snake_case; responses
 * are run through `toCamelCase` (matching the `features/DocumentSearch/api.ts`
 * template). Query keys are namespaced under `['groundTruth', ...]`.
 *
 * CASE-CONVERSION CAUTION (layer 5 rows): the row `payload` is the canonical
 * snake_case `LineItem` shape and carries free-form nested dicts (`meta`,
 * `field_verifications`) whose keys must survive untouched. We therefore send
 * row payloads RAW (no `toSnakeCase`) and read them RAW (no `toCamelCase`) —
 * the layer-1/2 splits, whose fields all round-trip cleanly, still use the
 * normal conversion helpers.
 */

/**
 * The row editor's default `model_class` — used when a caller doesn't pass
 * one explicitly (e.g. a hook rendered outside `RowEditor`).
 *
 * MUST stay in sync with the backend's `gt_layers.LINE_ITEM_MODEL_CLASS`
 * (`services/backend/src/sonance/gt_layers.py`, = `"LineItem"`). `RowEditor`
 * itself doesn't rely on this default: it resolves the doc's real
 * `model_class` from {@link useRowSchema} and passes it explicitly, so
 * non-`LineItem` docs (e.g. status reports) get their own model. (Distinct
 * from `ground_truth.py`'s `GROUND_TRUTH_MODEL_REGISTRY`, which maps doc-type
 * codes like `"tc"`/`"ad"` — a different layer.)
 */
export const LINE_ITEM_MODEL_CLASS = 'LineItem';

/**
 * Narrow an unknown response body to the list-of-objects shape the row /
 * snapshot hooks expect before we `.map` over it.
 *
 * These endpoints return a JSON array of envelopes, but a misbehaving server
 * (or a proxy returning `null` / an error object) would otherwise sail past the
 * `as Array<…>` cast and blow up with a cryptic destructure deep inside the
 * `.map`. Failing loud here keeps the error legible and surfaces it on React
 * Query's `error` (where the UI already handles failures) instead of crashing
 * the render. `context` is woven into the message so the offending endpoint is
 * obvious in logs.
 */
function asResponseArray(
    data: unknown,
    context: string,
): Array<Record<string, unknown>> {
    if (!Array.isArray(data)) {
        throw new Error(
            `Expected an array of ${context} from the server, got ${
                data === null ? 'null' : typeof data
            }.`,
        );
    }
    return data as Array<Record<string, unknown>>;
}

// Keeps the file page and its editors from refetching the same data when they mount a moment apart.
const FILE_PAGE_STALE_TIME_MS = 30_000;

/** Invalidates GT progress queries: overview and coverage by prefix, file status and docs for one file when its id is known, else for all files. */
export const invalidateGtProgress = (
    queryClient: QueryClient,
    ingestedFileId?: number | null,
) => {
    for (const key of ['overview', 'coverageStats']) {
        queryClient.invalidateQueries({ queryKey: ['groundTruth', key] });
    }
    for (const key of ['fileStatus', 'fileDocs']) {
        queryClient.invalidateQueries({
            queryKey:
                ingestedFileId != null
                    ? ['groundTruth', key, ingestedFileId]
                    : ['groundTruth', key],
        });
    }
};

/** Layer 0 — overview of every doc's per-layer GT status (team-scoped). */
export const useGroundTruthOverview = (params: GtOverviewParams = {}) => {
    const { client, enabled: authEnabled } = useAuthAxios();
    const {
        limit = 50,
        skip = 0,
        teamId,
        search,
        docType,
        fileTypes,
        gtStatus,
        sort,
        showCopies,
        showMissing,
        hasRemarks,
        enabled: callerEnabled = true,
    } = params;
    const enabled = authEnabled && callerEnabled;

    return useQuery({
        queryKey: [
            'groundTruth',
            'overview',
            {
                limit,
                skip,
                teamId,
                search,
                docType,
                fileTypes,
                gtStatus,
                sort,
                showCopies,
                showMissing,
                hasRemarks,
            },
        ],
        queryFn: async () => {
            const res = await client.get(`/api/ground-truth/overview`, {
                params: {
                    limit,
                    skip,
                    team_id: teamId ?? undefined,
                    search: search || undefined,
                    doc_type: docType || undefined,
                    file_types:
                        fileTypes && fileTypes.length > 0 ? fileTypes : undefined,
                    gt_status: gtStatus || undefined,
                    sort: sort || undefined,
                    show_copies: showCopies || undefined,
                    show_missing: showMissing || undefined,
                    has_remarks: hasRemarks || undefined,
                },
                paramsSerializer: { indexes: null },
            });
            return toCamelCase<GtOverviewResponse>(res.data);
        },
        enabled,
        // Keeps the current rows on screen while a new sort, filter or page
        // loads, instead of swapping the page for a loader and losing the scroll
        placeholderData: (previous) => previous,
    });
};

/**
 * Global GT annotation coverage stats for the dashboard section atop the
 * overview page — overall coverage %, per-`DocType` counts, and per-layer
 * (splitting/classification/extraction) completion.
 *
 * No `team_id` param: the backend scopes to the caller's team, the same files
 * `useGroundTruthOverview` lists.
 */
export const useGtCoverageStats = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'coverageStats'],
        queryFn: async () => {
            const res = await client.get(`/api/ground-truth/coverage-stats`);
            return toCamelCase<GtCoverageStats>(res.data);
        },
        enabled,
    });
};

/** How often reviewers changed Claude's split drafts, per reviewer and per drafted doc type. */
export const useGtDraftReviewStats = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'draftReviewStats'],
        queryFn: async () => {
            const res = await client.get(`/api/ground-truth/draft-review-stats`);
            return toCamelCase<GtDraftReviewStats>(res.data);
        },
        enabled,
    });
};

/** True when a GT request failed with HTTP 404 (the file or doc doesn't exist). */
export const isNotFoundError = (error: unknown) =>
    isAxiosError(error) && error.response?.status === 404;

/** Retry predicate for the GT file/doc queries: a 404 is final (the not-found page shows at once), other errors keep the default 3 tries. */
export const retryUnlessNotFound = (failureCount: number, error: unknown) =>
    !isNotFoundError(error) && failureCount < 3;

/** Single ingested file's GT status, fetched directly from the network (not the overview cache). */
export const useGtFileStatus = (ingestedFileId: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'fileStatus', ingestedFileId],
        queryFn: async () => {
            const res = await client.get(
                `/api/ground-truth/ingested-files/${ingestedFileId}`,
            );
            return toCamelCase<DocGtStatus>(res.data);
        },
        enabled: enabled && ingestedFileId !== null,
        staleTime: FILE_PAGE_STALE_TIME_MS,
        retry: retryUnlessNotFound,
    });
};

/** Saves the reviewer's remark on a whole file; blank clears it. */
export const useSaveGtFileRemark = (ingestedFileId: number | null) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (remark: string) => {
            if (ingestedFileId === null) {
                throw new Error(
                    'Cannot save the remark: ingestedFileId has not resolved yet.',
                );
            }
            const res = await client.put(
                `/api/ground-truth/ingested-files/${ingestedFileId}/remark`,
                { remark },
            );
            return toCamelCase<{ remark: string | null }>(res.data);
        },
        onSuccess: () => invalidateGtProgress(queryClient, ingestedFileId),
    });
};

/** File-level doc list for the GT hub's file page — every doc belonging to an ingested file, in page order. */
export const useGtFileDocs = (ingestedFileId: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'fileDocs', ingestedFileId],
        queryFn: async () => {
            const res = await client.get(
                `/api/ground-truth/ingested-files/${ingestedFileId}/docs`,
            );
            return toCamelCase<GtFileDoc[]>(res.data);
        },
        enabled: enabled && ingestedFileId !== null,
        staleTime: FILE_PAGE_STALE_TIME_MS,
        retry: retryUnlessNotFound,
    });
};

/** Content versions of the file's name, oldest first; empty when there is only one. */
export const useGtFileVersions = (ingestedFileId: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'fileVersions', ingestedFileId],
        queryFn: async () => {
            const res = await client.get(
                `/api/ground-truth/ingested-files/${ingestedFileId}/versions`,
            );
            return toCamelCase<GtFileVersion[]>(res.data);
        },
        enabled: enabled && ingestedFileId !== null,
        staleTime: FILE_PAGE_STALE_TIME_MS,
        retry: retryUnlessNotFound,
    });
};

/** Answers on the file that no current doc covers, grouped by pages, type and kind. */
export const useGtUncoveredAnswers = (ingestedFileId: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'uncoveredAnswers', ingestedFileId],
        queryFn: async () => {
            const res = await client.get(
                `/api/ground-truth/ingested-files/${ingestedFileId}/uncovered-answers`,
            );
            return toCamelCase<GtUncoveredAnswer[]>(res.data);
        },
        enabled: enabled && ingestedFileId !== null,
        staleTime: FILE_PAGE_STALE_TIME_MS,
        retry: retryUnlessNotFound,
    });
};

/** Raised when the Hub already holds a file with the same bytes; carries the existing row's id. */
export class DuplicateHubFileError extends Error {
    constructor(public readonly ingestedFileId: number) {
        super('This file is already in the Hub');
    }
}

/**
 * Upload a PDF or Excel file straight into the Hub (no ingestion). A 409 is
 * rethrown as {@link DuplicateHubFileError} so the caller can link to the
 * existing file; the overview refreshes on success.
 */
export const useUploadGroundTruthFile = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('file', file);
            try {
                const res = await client.post(
                    '/api/ground-truth/ingested-files/upload',
                    formData,
                );
                return toCamelCase<GtHubUploadResponse>(res.data);
            } catch (error) {
                if (
                    isAxiosError(error) &&
                    error.response?.status === 409 &&
                    typeof error.response.data?.ingested_file_id === 'number'
                ) {
                    throw new DuplicateHubFileError(
                        error.response.data.ingested_file_id,
                    );
                }
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['groundTruth', 'overview'],
            });
        },
    });
};

/**
 * Bulk-mark (or unmark) every doc under an ingested file as ground truth, from the
 * overview file row's tri-state checkbox.
 *
 * `ingestedFileId` travels as a mutate-time argument (not a hook param) since the
 * overview renders one row per file and a single hook instance handles every row's
 * toggle. On success, invalidates the overview (whose `markedCount` rollup must
 * refresh so the checkbox re-derives its checked/indeterminate state from the
 * server) and the specific file's docs query if it's been loaded (expanded).
 */
export const useBulkMarkGroundTruth = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ ingestedFileId, groundTruth }: GtBulkMarkArgs) => {
            const res = await client.put(
                `/api/ground-truth/ingested-files/${ingestedFileId}/ground-truth`,
                toSnakeCase({ groundTruth }),
            );
            return toCamelCase<GtBulkMarkResponse>(res.data);
        },
        onSuccess: (_data, variables) => {
            invalidateGtProgress(queryClient, variables.ingestedFileId);
        },
    });
};

/**
 * Toggle a single doc's `ground_truth` flag, from the expanded doc sub-row's
 * checkbox — the per-doc counterpart to {@link useBulkMarkGroundTruth}.
 *
 * `ingestedFileId` is a hook param (not a mutate-time argument) purely so
 * `onSuccess` can invalidate that one file's docs query.
 */
export const useMarkDocGroundTruth = (ingestedFileId: number | null) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ docId, groundTruth }: GtMarkDocArgs) => {
            const res = await client.put(
                `/api/ground-truth/docs/${docId}/ground-truth`,
                toSnakeCase({ groundTruth }),
            );
            return toCamelCase<GtBulkMarkResponse>(res.data);
        },
        onSuccess: () => {
            invalidateGtProgress(queryClient, ingestedFileId);
        },
    });
};

/** Layer 1 — page-range splits for an ingested file. */
export const useGtPageSplits = (ingestedFileId: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'pageSplits', ingestedFileId],
        queryFn: async () => {
            const res = await client.get(
                `/api/ground-truth/ingested-files/${ingestedFileId}/splits`,
            );
            return toCamelCase<GtPageSplit[]>(res.data);
        },
        enabled: enabled && ingestedFileId !== null,
        staleTime: FILE_PAGE_STALE_TIME_MS,
    });
};

/** Pages left out of every split of an ingested file on purpose. */
export const useGtLeftOutPages = (ingestedFileId: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'leftOutPages', ingestedFileId],
        queryFn: async () => {
            const res = await client.get(
                `/api/ground-truth/ingested-files/${ingestedFileId}/left-out-pages`,
            );
            return toCamelCase<GtLeftOutPages[]>(res.data);
        },
        enabled: enabled && ingestedFileId !== null,
        staleTime: FILE_PAGE_STALE_TIME_MS,
    });
};

/**
 * Real PDF page count for an ingested file, used by `PageSplitEditor` to
 * bound the end-page input against the document's actual length.
 *
 * The endpoint 4xxs for non-PDF sources (e.g. Excel), which is an expected,
 * non-actionable outcome for this hook's caller — the editor's other
 * validations don't depend on the page count, so `retry: false` avoids
 * hammering an endpoint that will keep failing the same way.
 */
export const useGtPageCount = (ingestedFileId: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'pageCount', ingestedFileId],
        queryFn: async () => {
            const res = await client.get(
                `/api/ground-truth/ingested-files/${ingestedFileId}/page-count`,
            );
            return toCamelCase<GtPageCountResponse>(res.data);
        },
        enabled: enabled && ingestedFileId !== null,
        staleTime: FILE_PAGE_STALE_TIME_MS,
        retry: false,
    });
};

/** Layer 2 — sheet-level splits for an ingested file. */
export const useGtSheetSplits = (ingestedFileId: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'sheetSplits', ingestedFileId],
        queryFn: async () => {
            const res = await client.get(
                `/api/ground-truth/ingested-files/${ingestedFileId}/sheet-splits`,
            );
            return toCamelCase<GtSheetSplit[]>(res.data);
        },
        enabled: enabled && ingestedFileId !== null,
        staleTime: FILE_PAGE_STALE_TIME_MS,
    });
};

/**
 * Grid-ready column descriptors for a doc's resolved row-truth model, from
 * `GET .../docs/{doc_id}/rows/schema` — lets the row editor render generically
 * off the doc's actual model shape (LineItem, StatusReportRowTruth, …) instead
 * of hardcoding one. `RowEditor` fetches this first and feeds its
 * `modelClass`/`columns` into `useGtRows`/`useGtSuggestedRows`/`useSaveGtRows`
 * below.
 */
export const useRowSchema = (docId: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'rowSchema', docId],
        queryFn: async () => {
            const res = await client.get(
                `/api/ground-truth/docs/${docId}/rows/schema`,
            );
            return toCamelCase<RowModelSchema>(res.data);
        },
        enabled: enabled && docId !== null,
    });
};

/** True when a GT request failed with HTTP 403 (no permission to manage ground truth). */
export const isForbiddenError = (error: unknown) =>
    isAxiosError(error) && error.response?.status === 403;

/**
 * Read a `header/custom` body. `values` is peeled off before the envelope is
 * camelCased because its keys are reviewer-chosen (`toCamelCase` would turn
 * `field1` into `field_1`); anything that is not the expected envelope (e.g.
 * an empty array from a stubbed endpoint) reads as "no field group".
 */
function parseCustomHeader(data: unknown): CustomHeader {
    const none: CustomHeader = {
        schema: null,
        values: {},
        storedVersion: null,
    };
    if (data === null || typeof data !== 'object' || Array.isArray(data)) {
        return none;
    }
    const { values, schema, ...rest } = data as Record<string, unknown>;
    const envelope = toCamelCase<{ storedVersion?: number | null }>(rest);
    return {
        schema: schema
            ? toCamelCase<CustomHeaderSchema>(schema as Record<string, unknown>)
            : null,
        values: (values ?? {}) as Record<string, unknown>,
        storedVersion: envelope.storedVersion ?? null,
    };
}

/** Header-level custom field values and the schema of the doc type's header field group. */
export const useHeaderCustom = (docId: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'headerCustom', docId],
        queryFn: async () => {
            const res = await client.get(
                `/api/ground-truth/docs/${docId}/header/custom`,
            );
            return parseCustomHeader(res.data);
        },
        enabled: enabled && docId !== null,
        // A 403 is final: the section shows a no-permission notice at once.
        retry: (failureCount, error) =>
            !isForbiddenError(error) && failureCount < 3,
    });
};

/**
 * Save custom header values. `values` is sent untouched (reviewer-chosen keys
 * must not be case-converted); a `null` on an earlier-version key clears it.
 * Registered doc types 409 until their built-in header has been saved.
 */
export const useSaveHeaderCustom = (docId: number | null) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (values: Record<string, unknown>) => {
            if (docId === null) {
                throw new Error(
                    'Cannot save custom fields: docId has not resolved yet.',
                );
            }
            const res = await client.put(
                `/api/ground-truth/docs/${docId}/header/custom`,
                { values },
            );
            return parseCustomHeader(res.data);
        },
        onSuccess: (saved) => {
            invalidateGtProgress(queryClient);
            queryClient.setQueryData(
                ['groundTruth', 'headerCustom', docId],
                saved,
            );
        },
    });
};

/** Layer 5 — extracted rows for a document.
 *
 * The envelope fields (`id`, `docId`, `modelClass`, `rowIndex`, …) are
 * camelCased, but each `payload` is preserved RAW (the canonical snake_case
 * row-truth shape). Running `payload` through `toCamelCase` would mangle the
 * free-form nested `meta` / `field_verifications` keys and break the
 * write-back round trip, so we convert only the envelope.
 *
 * `modelClass` is nullable so a caller (namely `RowEditor`) can defer fetching
 * until it has resolved the doc's real model_class from `useRowSchema` — a
 * `null` disables the query the same way a `null` `docId` does. */
export const useGtRows = (
    docId: number | null,
    modelClass: string | null = LINE_ITEM_MODEL_CLASS,
) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'rows', docId, modelClass],
        queryFn: async () => {
            const res = await client.get(
                `/api/ground-truth/docs/${docId}/rows`,
                {
                    params: { model_class: modelClass },
                },
            );
            const raw = asResponseArray(res.data, 'rows');
            return raw.map((row) => {
                const { payload, ...envelope } = row;
                return {
                    ...toCamelCase<Omit<GtRow, 'payload'>>(envelope),
                    payload: (payload ?? {}) as GtRowPayload,
                };
            }) as GtRow[];
        },
        enabled: enabled && docId !== null && modelClass !== null,
    });
};

/**
 * Layer 5 — the model's own extracted rows for a doc, for seeding or resetting
 * the row grid from a suggestion instead of a blank one. `enabled` is
 * hard-`false` because suggestions are fetched on demand via `refetch()`, not
 * on mount. `modelClass` is nullable for the same reason as `useGtRows`'s —
 * `refetch()` throws if called before it resolves, rather than silently
 * hitting the endpoint mid-load.
 */
export const useGtSuggestedRows = (
    docId: number | null,
    modelClass: string | null = LINE_ITEM_MODEL_CLASS,
) => {
    const { client } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'suggestedRows', docId, modelClass],
        queryFn: async () => {
            if (docId === null) {
                throw new Error(
                    'Cannot fetch suggested rows: docId has not resolved yet.',
                );
            }
            if (modelClass === null) {
                throw new Error(
                    'Cannot fetch suggested rows: row model schema has not resolved yet.',
                );
            }
            // No `model_class` query param on the request itself — unlike
            // `/rows`, the `/rows/suggested` endpoint only sources `LineItem`
            // rows today (see its route docstring), so the backend doesn't
            // accept one. `modelClass` is still checked above so `refetch()`
            // can't fire before the doc's schema has resolved.
            const res = await client.get(
                `/api/ground-truth/docs/${docId}/rows/suggested`,
            );
            return asResponseArray(res.data, 'suggested rows');
        },
        enabled: false,
        retry: false,
    });
};

// ── Write / mutation hooks ─────────────────────────────────────────────────────

/**
 * Layer 1 — replace ALL page-range splits for an ingested file.
 *
 * Mirrors the `useUpdateGroundTruth` template: PUT, then invalidate the
 * matching read key and the overview (whose per-layer counts must refresh).
 */
/** Query params for a layer save: `confirm_clear` lets an empty set delete saved answers, `base_version` guards against a stale save, `prefilled` marks a pipeline pre-fill. */
const saveParams = (
    confirmClear?: boolean,
    baseVersion?: number,
    prefilled?: boolean,
) => ({
    params: {
        ...(confirmClear ? { confirm_clear: true } : {}),
        ...(baseVersion !== undefined ? { base_version: baseVersion } : {}),
        ...(prefilled ? { prefilled: true } : {}),
    },
});

/** `prefilledRef` says the rows being saved are the pipeline's pre-fill, so the backend marks them as its own. */
export const useSaveGtPageSplits = (
    ingestedFileId: number | null,
    prefilledRef?: { readonly current: boolean },
) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            payload: splits,
            confirmClear,
            baseVersion,
        }: GtSaveRequest<GtPageSplitInput[]>) => {
            if (ingestedFileId === null) {
                throw new Error(
                    'Cannot save page splits: ingestedFileId has not resolved yet.',
                );
            }
            const res = await client.put(
                `/api/ground-truth/ingested-files/${ingestedFileId}/splits`,
                toSnakeCase(splits),
                saveParams(confirmClear, baseVersion, prefilledRef?.current),
            );
            return toCamelCase<GtPageSplit[]>(res.data);
        },
        onSuccess: (saved) => {
            invalidateGtProgress(queryClient, ingestedFileId);
            // Seeded from the response so the clear confirm counts these even if the refetch fails.
            queryClient.setQueryData(
                ['groundTruth', 'pageSplits', ingestedFileId],
                saved,
            );
            queryClient.invalidateQueries({
                queryKey: ['groundTruth', 'pageSplits', ingestedFileId],
            });
            queryClient.invalidateQueries({
                queryKey: ['groundTruth', 'uncoveredAnswers', ingestedFileId],
            });
        },
    });
};

/** Replaces the pages left out of every split; refused (422) when one is in a saved split. */
export const useSaveGtLeftOutPages = (ingestedFileId: number | null) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (leftOut: GtLeftOutPages[]) => {
            if (ingestedFileId === null) {
                throw new Error(
                    'Cannot save left-out pages: ingestedFileId has not resolved yet.',
                );
            }
            const res = await client.put(
                `/api/ground-truth/ingested-files/${ingestedFileId}/left-out-pages`,
                toSnakeCase(leftOut),
            );
            return toCamelCase<GtLeftOutPages[]>(res.data);
        },
        onSuccess: (saved) => {
            queryClient.setQueryData(
                ['groundTruth', 'leftOutPages', ingestedFileId],
                saved,
            );
        },
    });
};

/** Whether "create docs from splits" may replace this file's docs (the backend guard, asked before the click). */
export const useGtCreateDocsCheck = (ingestedFileId: number | null) => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'createDocsCheck', ingestedFileId],
        queryFn: async () => {
            const res = await client.get(
                `/api/ground-truth/ingested-files/${ingestedFileId}/create-docs-check`,
            );
            return toCamelCase<GtCreateDocsCheck>(res.data);
        },
        enabled: enabled && ingestedFileId !== null,
    });
};

/**
 * Layer 3 — REPLACE all of an ingested file's docs with ones derived from its
 * persisted layer-1 page-range splits.
 *
 * Destructive on the backend: existing docs (and anything layered on top of
 * them — headers, rows) are deleted before the new ones are created, guarded
 * server-side by a 409 when production data is attached. Callers must confirm
 * with the operator first — see `PageSplitEditor`'s "Create documents from
 * these splits" action. No request body.
 *
 * On success, invalidates the file's docs list (feeds the doc switcher / the
 * single-doc auto-forward) and the overview (layer-status strip + per-layer
 * counts) so the new docs and refreshed layer status show without a reload.
 */
export const useCreateGtDocsFromSplits = (ingestedFileId: number | null) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (ingestedFileId === null) {
                throw new Error(
                    'Cannot create docs from splits: ingestedFileId has not resolved yet.',
                );
            }
            const res = await client.post(
                `/api/ground-truth/ingested-files/${ingestedFileId}/create-docs`,
            );
            return toCamelCase<GtCreatedDoc[]>(res.data);
        },
        onSuccess: () => {
            invalidateGtProgress(queryClient, ingestedFileId);
            queryClient.invalidateQueries({
                queryKey: ['groundTruth', 'createDocsCheck', ingestedFileId],
            });
            queryClient.invalidateQueries({
                queryKey: ['groundTruth', 'uncoveredAnswers', ingestedFileId],
            });
        },
    });
};

/** Layer 2 — replace ALL sheet-level splits for an ingested file. */
export const useSaveGtSheetSplits = (ingestedFileId: number | null) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            payload: sheetSplits,
            confirmClear,
            baseVersion,
        }: GtSaveRequest<GtSheetSplitInput[]>) => {
            if (ingestedFileId === null) {
                throw new Error(
                    'Cannot save sheet splits: ingestedFileId has not resolved yet.',
                );
            }
            const res = await client.put(
                `/api/ground-truth/ingested-files/${ingestedFileId}/sheet-splits`,
                toSnakeCase(sheetSplits),
                saveParams(confirmClear, baseVersion),
            );
            return toCamelCase<GtSheetSplit[]>(res.data);
        },
        onSuccess: (saved) => {
            invalidateGtProgress(queryClient, ingestedFileId);
            queryClient.setQueryData(
                ['groundTruth', 'sheetSplits', ingestedFileId],
                saved,
            );
            queryClient.invalidateQueries({
                queryKey: ['groundTruth', 'sheetSplits', ingestedFileId],
            });
        },
    });
};

/**
 * Layer 5 — replace ALL extracted rows for a doc.
 *
 * The body is the OBJECT `{ model_class, rows }` (NOT a bare array). Row
 * payloads are sent RAW (snake_case row-truth shape) — see the case-conversion
 * caution at the top of this file — so the envelope is hand-built rather than
 * piped through `toSnakeCase`. `modelClass` is nullable (the caller may not
 * have resolved the doc's schema yet) — `mutationFn` guards it the same way it
 * guards `docId`, before either reaches the request.
 */
export const useSaveGtRows = (
    docId: number | null,
    modelClass: string | null = LINE_ITEM_MODEL_CLASS,
) => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            payload: rows,
            confirmClear,
            baseVersion,
        }: GtSaveRequest<GtRowPayload[]>) => {
            if (docId === null) {
                throw new Error(
                    'Cannot save rows: docId has not resolved yet.',
                );
            }
            if (modelClass === null) {
                throw new Error(
                    'Cannot save rows: row model schema has not resolved yet.',
                );
            }
            const res = await client.put(
                `/api/ground-truth/docs/${docId}/rows`,
                { model_class: modelClass, rows },
                saveParams(confirmClear, baseVersion),
            );
            const raw = asResponseArray(res.data, 'rows');
            return raw.map((row) => {
                const { payload, ...envelope } = row;
                return {
                    ...toCamelCase<Omit<GtRow, 'payload'>>(envelope),
                    payload: (payload ?? {}) as GtRowPayload,
                };
            }) as GtRow[];
        },
        onSuccess: (saved) => {
            invalidateGtProgress(queryClient);
            queryClient.setQueryData(
                ['groundTruth', 'rows', docId, modelClass],
                saved,
            );
            queryClient.invalidateQueries({
                queryKey: ['groundTruth', 'rows', docId, modelClass],
            });
        },
    });
};

// ── Snapshots ────────────────────────────────────────────────────────────────

/** Query key shared by the snapshot read hook and the two write hooks' invalidation. */
const SNAPSHOTS_KEY = ['groundTruth', 'snapshots'] as const;

/** True when the snapshots backend reported its LakeFS dependency is down (502). */
export const isSnapshotsUnavailable = (error: unknown): boolean =>
    isAxiosError(error) && error.response?.status === 502;

/**
 * List published GT snapshots (newest first).
 *
 * `counts` is a free-form `{ tableName: rowCount }` map whose keys are literal
 * backend table names — they must NOT be run through `toCamelCase` or they'd be
 * mangled, so each entry's envelope is converted and `counts` is passed through
 * raw (same split-the-envelope approach the layer-5 row hooks use).
 *
 * The endpoint can return **502** (not 500) when LakeFS is unreachable; React
 * Query surfaces that on `error`, and `isSnapshotsUnavailable(error)` lets the
 * UI distinguish the "snapshots service down" state from a generic failure.
 */
export const useSnapshots = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: SNAPSHOTS_KEY,
        queryFn: async () => {
            const res = await client.get(`/api/ground-truth/snapshots`);
            const raw = asResponseArray(res.data, 'snapshots');
            return raw.map((snap) => {
                const { counts, ...envelope } = snap;
                return {
                    ...toCamelCase<Omit<GtSnapshot, 'counts'>>(envelope),
                    counts: (counts ?? {}) as Record<string, number>,
                };
            }) as GtSnapshot[];
        },
        enabled,
        // A 502 means LakeFS is down, not that the request was malformed —
        // don't hammer it with retries; let the UI show the unavailable state.
        retry: false,
    });
};

/**
 * Trigger a Dagster job that exports the current GT to a new snapshot.
 *
 * `name` is REQUIRED (server 422s without it) — never defaulted here.
 * Returns immediately with the `{ run_id }` — there's no run-status endpoint, so
 * the UI does NOT poll to completion; it just reports the run id and that the
 * job runs in the background. On success the snapshot list is invalidated so the
 * new (still-exporting, `exportedAt: null`) row appears.
 */
export const useExportSnapshot = () => {
    const { client } = useAuthAxios();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ name, description }: GtSnapshotExportArgs) => {
            const res = await client.post(
                `/api/ground-truth/snapshots/export`,
                toSnakeCase({ name, description }),
            );
            return toCamelCase<GtSnapshotJobResponse>(res.data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: SNAPSHOTS_KEY });
        },
    });
};

/**
 * Pre-export checklist: what an export started now would leave out. Read-only.
 *
 * Fetched fresh each time the export dialog opens (`enabled`), since the
 * answers can change between opens.
 */
export const useSnapshotExportCheck = (enabled: boolean) => {
    const { client, enabled: authEnabled } = useAuthAxios();

    return useQuery({
        queryKey: [...SNAPSHOTS_KEY, 'exportCheck'],
        queryFn: async () => {
            const res = await client.get(
                `/api/ground-truth/snapshots/export-check`,
            );
            return toCamelCase<GtExportCheckReport>(res.data);
        },
        enabled: enabled && authEnabled,
        staleTime: 0,
        gcTime: 0,
        retry: false,
    });
};

/**
 * DESTRUCTIVE: restore a snapshot into `targetBucket`, rewriting object keys in
 * that target DB. `targetBucket` is REQUIRED — the server returns 422 if it's
 * missing, so it is NEVER defaulted here. Returns immediately with the Dagster
 * `{ run_id }`.
 */
export const useRestoreSnapshot = () => {
    const { client } = useAuthAxios();

    return useMutation({
        mutationFn: async ({
            snapshotDate,
            targetBucket,
            mode,
        }: GtSnapshotRestoreArgs) => {
            const res = await client.post(
                `/api/ground-truth/snapshots/${snapshotDate}/restore`,
                toSnakeCase({ targetBucket, mode }),
            );
            return toCamelCase<GtSnapshotJobResponse>(res.data);
        },
        // A 502 means LakeFS is down, not a malformed request — don't retry it,
        // matching `useSnapshots`. Surface the failure to the UI immediately.
        retry: false,
    });
};

/** How often `useSnapshotRuns` polls — a fixed interval is fine for this small UI. */
const SNAPSHOT_RUN_POLL_INTERVAL_MS = 5000;

/** Dagster run states that mean "still executing" — everything else is resolved. */
const ACTIVE_RUN_STATUSES: ReadonlySet<SnapshotRunStatus['status']> = new Set([
    'QUEUED',
    'NOT_STARTED',
    'MANAGED',
    'STARTING',
    'STARTED',
    'CANCELING',
]);

/** True when a snapshot run is still in flight (not yet SUCCESS/FAILURE/CANCELED). */
export const isActiveSnapshotRun = (run: SnapshotRunStatus): boolean =>
    ACTIVE_RUN_STATUSES.has(run.status);

/**
 * Poll recent export/restore Dagster runs for GT snapshots (newest first), so
 * `SnapshotPanel` can show whether a run is currently in progress.
 *
 * Fixed-interval polling (no backoff) — this is a small observability widget,
 * not a long-running job tracker.
 *
 * Polling is unconditional (not gated on "a run was just triggered") — an intentional simplicity tradeoff for now.
 */
export const useSnapshotRuns = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['groundTruth', 'snapshotRuns'],
        queryFn: async () => {
            const res = await client.get(`/api/ground-truth/snapshots/runs`);
            const raw = asResponseArray(res.data, 'snapshot runs');
            return raw.map((run) => toCamelCase<SnapshotRunStatus>(run));
        },
        enabled,
        refetchInterval: SNAPSHOT_RUN_POLL_INTERVAL_MS,
    });
};
